import type {
  AuditField,
  AuditQualifier,
  CodedFields,
  ReasonOption,
} from "../types";
import {
  R_ASA,
  R_ICD,
  R_ICD_ADDL,
  R_MOD,
  R_PHYS,
  R_PROC,
  R_SHARED,
} from "./reasons";

// Reviewable field definitions + comparison helpers (HUB-504), ported from the
// prototype. `AUDIT_FIELDS` is the display order and the basis for the per-case
// discrepancy count. Comparison is symmetric, so it never reveals which result
// is the agent vs the coder (blind-safe).
//
// A FIELD BELONGS HERE ONLY IF BOTH SIDES CAN PRODUCE IT. Symmetry is what
// makes the slots opaque: a field only one leg fills makes the populated slot
// self-identifying, and the A/B arrangement is stable per case, so one such
// case hands the reviewer the arrangement for every later one. "Ancillary
// procedures" was removed for exactly that — the agent emitted it and the human
// coder's feed has no source for it. See CodedFields in ../types.ts.

export const AUDIT_FIELDS: AuditField[] = [
  {
    key: "surgical_cpt",
    label: "Surgical CPT",
    kind: "cpt",
    multi: false,
    reasons: R_PROC,
    help: {
      tip: "Guidance for selecting the surgical CPT — approach, laterality, and bundling.",
      href: "https://www.aapc.com/codes/cpt-codes-range/",
    },
  },
  {
    key: "anesthesia_cpt",
    label: "Anesthesia CPT (ASA)",
    kind: "asa",
    multi: false,
    reasons: R_ASA,
    help: {
      tip: "ASA crosswalk & base-unit guidance for the anesthesia code.",
      href: "https://www.asahq.org/standards-and-practice-parameters",
    },
  },
  {
    key: "primary_icd",
    label: "Primary diagnosis (ICD-10)",
    kind: "icd",
    multi: false,
    reasons: R_ICD,
    help: {
      tip: "ICD-10-CM primary-diagnosis sequencing and specificity rules.",
      href: "https://www.cms.gov/medicare/coding-billing/icd-10-codes",
    },
  },
  {
    key: "additional_icd",
    optional: true,
    label: "Additional diagnoses",
    kind: "icd",
    multi: true,
    reasons: R_ICD_ADDL,
    help: {
      tip: "Secondary-diagnosis rules — Excludes1 conflicts, combo codes, supporting dx.",
      href: "https://www.cms.gov/medicare/coding-billing/icd-10-codes",
    },
  },
  {
    key: "modifiers",
    optional: true,
    label: "Modifiers",
    kind: "mod",
    multi: true,
    reasons: R_MOD,
    help: {
      tip: "Anesthesia modifiers (AA/QK/QX/QY/QZ) and physical-status/qualifying modifiers.",
      href: "https://www.aapc.com/codes/hcpcs-modifiers",
    },
  },
  {
    key: "phys_status",
    label: "Physical status (ASA)",
    kind: "phys",
    multi: false,
    reasons: R_PHYS,
    help: {
      tip: "ASA physical-status classification (P1–P6) and documentation support.",
      href: "https://www.asahq.org/standards-and-practice-parameters/statement-on-asa-physical-status-classification-system",
    },
  },
];

/** Anesthesia qualifier flags — boolean fields compared A vs B like any field. */
export const AUDIT_QUALIFIERS: AuditQualifier[] = [
  { key: "emergency", label: "Emergency", sub: "Qualifying circ. 99140" },
  {
    key: "unusual_position",
    label: "Unusual position",
    sub: "e.g. prone / sitting",
  },
];

/** Set equality (order-independent) — for multi-value fields. */
export function sameSet(
  a: string[] | undefined,
  b: string[] | undefined,
): boolean {
  const x = a || [];
  const y = b || [];
  if (x.length !== y.length) return false;
  const s = new Set(x);
  return y.every((v) => s.has(v));
}

/**
 * An FA procedure suffix: exactly five digits followed by letters.
 *
 * FA appends a local annotation to the CPT — 45378DG, 45378ROB — and the agent
 * never emits one (measured over production: coder 3,725 suffixed, agent 0). So
 * every one of those was scored as a full disagreement about the procedure when
 * both sides had named the same procedure.
 *
 * The pattern is deliberately narrow. "Strip anything after five characters"
 * would corrupt real codes that are not five digits:
 *
 *   0449T, 0571T, 0823T   Category III CPT — four digits then T
 *   D0120                 CDT (dental) — a letter then four digits
 *   ZZZZZ                 FA's own sentinel, 1,249 of them
 *
 * Requiring \d{5} before the letters leaves all of those untouched.
 */
const FA_SUFFIXED_CODE = /^(\d{5})[A-Za-z]+$/;

/**
 * A procedure code with FA's local annotation removed.
 *
 * Used for BOTH comparison and display. The display half was decided
 * 2026-08-26 and reverses what this comment used to say.
 *
 * The annotation is real information FA recorded, and showing it was the
 * intent — until it became clear that ONLY the coder leg produces one (3,725
 * against the agent's 0). That makes any annotated code a per-case marker of
 * which slot is the human, and because the A/B arrangement is stable per case,
 * one such chip identifies the leg for every case that reviewer opens
 * afterwards. It is the same defect `ancillary` was removed for.
 *
 * The raw value is untouched server-side, so nothing is lost from the record —
 * only from the reviewer's view, which is exactly where it did harm.
 */
export function comparableCode(value: string | undefined): string | undefined {
  if (!value) return value;
  const m = FA_SUFFIXED_CODE.exec(value.trim());
  return m ? m[1] : value.trim();
}

/**
 * ICD-10 comparison key: formatting removed so two vintages compare equal.
 */
function icdKey(v: string): string {
  return v.trim().toUpperCase().replace(/\./g, "");
}

/**
 * The shortest ICD code allowed to match a longer one.
 *
 * ICD-10-CM categories are three characters (M17, Z12). Letting those match
 * their descendants would make a bare category agree with every code beneath
 * it — and coding to a category instead of a billable leaf is exactly the error
 * an audit should catch.
 */
const MIN_ICD_PREFIX = 4;

/**
 * Whether two diagnosis codes are the same code, allowing a vintage difference.
 *
 * The agent codes against FY2026 ICD-10-CM. Families expanded in the Oct-2025
 * update — Z86.010 becoming Z86.0100/0101/… — otherwise read as parent-child
 * disagreements against a feed on the older set, which is a difference in
 * code-set vintage rather than in clinical judgement.
 */
export function sameICDCode(x: string, y: string): boolean {
  let a = icdKey(x);
  let b = icdKey(y);
  if (a === b) return true;
  if (a.length > b.length) [a, b] = [b, a];
  return a.length >= MIN_ICD_PREFIX && b.startsWith(a);
}

/** Set comparison for diagnoses, pairing greedily across vintages. */
export function sameICDSet(
  x: string[] | undefined,
  y: string[] | undefined,
): boolean {
  const a = (x || []).filter(Boolean);
  const b = (y || []).filter(Boolean);
  if (a.length !== b.length) return false;
  const used = new Array<boolean>(b.length).fill(false);
  return a.every((code) => {
    const i = b.findIndex(
      (other, idx) => !used[idx] && sameICDCode(code, other),
    );
    if (i === -1) return false;
    used[i] = true;
    return true;
  });
}

/**
 * How two results relate on one field.
 *
 * Three states rather than two, because "not comparable" and "agrees" mean
 * opposite things for the study and only one of them belongs in a discrepancy
 * count.
 */
export type FieldAgreement = "agree" | "differ" | "not-comparable";

/**
 * Apply the comparison rules to one field.
 *
 * Mirrors readmodel.AgreementFor on the server, which computes the counts the
 * queue renders. The two must agree: a compare screen that calls a field a
 * match while the card behind it counts a discrepancy teaches a reviewer to
 * distrust both.
 */
export function fieldAgreement(
  field: AuditField,
  A: CodedFields,
  B: CodedFields,
): FieldAgreement {
  const va = A[field.key];
  const vb = B[field.key];

  // asa_modifiers is INFERRED, not extracted: the agent predicts one from
  // staffing signals when the chart is silent, while FA carries only explicitly
  // entered modifiers — 18% against 3% in production. A one-sided modifier is
  // one side answering a question the other was not asked, not a disagreement.
  if (field.kind === "mod") {
    const a = ((va as string[]) || []).filter(Boolean);
    const b = ((vb as string[]) || []).filter(Boolean);
    if (a.length === 0 || b.length === 0) return "not-comparable";
    return sameSet(a, b) ? "agree" : "differ";
  }

  if (field.kind === "icd") {
    const agrees = field.multi
      ? sameICDSet(va as string[], vb as string[])
      : sameICDCode((va as string) || "", (vb as string) || "");
    return agrees ? "agree" : "differ";
  }

  return fieldMatch(field, A, B) ? "agree" : "differ";
}

/** Whether a single field agrees between two results. Symmetric in A/B. */
export function fieldMatch(
  field: AuditField,
  A: CodedFields,
  B: CodedFields,
): boolean {
  const va = A[field.key];
  const vb = B[field.key];
  if (field.multi) return sameSet(va as string[], vb as string[]);
  // CPT-shaped fields compare on the code, not on FA's annotation of it.
  if (field.kind === "cpt" || field.kind === "asa") {
    return (
      comparableCode(va as string | undefined) ===
      comparableCode(vb as string | undefined)
    );
  }
  return va === vb;
}

/**
 * Number of compared fields where two results DIFFER. Blind-safe.
 *
 * Counts only "differ": a not-comparable field is not a disagreement, and
 * counting it as one is what made every inferred modifier look like the agent
 * getting it wrong.
 */
export function discrepancyCount(A: CodedFields, B: CodedFields): number {
  return AUDIT_FIELDS.filter((f) => fieldAgreement(f, A, B) === "differ")
    .length;
}

/** The field-specific reason library plus the shared library (review slice). */
export function reasonsFor(field: AuditField): ReasonOption[] {
  return [...field.reasons, ...R_SHARED];
}
